import betChips from 'assets/sounds/bet_chips.wav';
import check from 'assets/sounds/check.wav';
import chipsToPot from 'assets/sounds/chips_to_pot.wav';
// Winner rakes the pot at hand end. NOT a duplicate of chips_to_pot, which is
// each bet gathered INTO the pot between rounds — one is a wager, the other is
// the payoff, and the payoff is deliberately longer and one tier louder.
// Extracted losslessly from the audio.aiff sprite master (offset 11.99s).
import collectChips from 'assets/sounds/collect_chips.wav';
import dealingCards from 'assets/sounds/dealing_cards.wav';
import fold from 'assets/sounds/fold.wav';
// The bet/raise tap — the tray opening AND the confirm, the two moments the
// old sprite `notify` marked. This is the owner's cut, codified 2026-08-27
// from his tweak-panel settings (gain -5.5dB, brightness 5150 on the softened
// master): it lands at -33.8, beside the fold tier — the commit as a whisper.
import placingBet from 'assets/sounds/placing_bet.wav';
import playerFlipCards from 'assets/sounds/player_flip_cards.wav';
// NOTE: player_out_of_time.wav is deliberately NOT wired up. It was the cue the
// owner kept hearing as "the bell" (EN-10585): 1614ms, peak 0.7, and 16.7% of
// its energy in five partials, so it rings. The countdown already says the
// clock is running out and the fold cue says how the turn ended, which makes it
// a third announcement of something already said twice. The asset is still in
// assets/sounds if it is ever wanted back — re-import it here to restore.
import playerTimeWarning from 'assets/sounds/player_time_warning.wav';
import playerWins from 'assets/sounds/player_wins.wav';
import playersTurn from 'assets/sounds/players_turn.wav';
// KEPT FROM THE SPRITE SHEET, NOT IMPLEMENTED — a riffle. Extracted losslessly
// from the AIFF master and levelled before the sheet was deleted (2026-08-27),
// held against a future sound pass. Nothing plays it.
import shuffle from 'assets/sounds/shuffle.wav';
// THE SINGLE-CHIP WAGER. Any bet at or under the table's big blind — the blinds
// themselves, a limp, a min bet, a call of one — where bet_chips is a whole
// handful. Everything above the blind keeps bet_chips, so a limped pot and a
// raised pot no longer sound identical (owner, 8/28); see
// utils/tableSfx.resolveChipCue for the rule and Table/Bet.tsx for the wiring.
// Came off the sprite sheet as `bet_alt`, renamed when it was implemented.
import smallBet from 'assets/sounds/small_bet.wav';
// KEPT FROM THE SPRITE SHEET, NOT IMPLEMENTED — an 80ms card click. Same
// provenance and status as shuffle above.
import tableCard from 'assets/sounds/table_card.wav';
import tableFlipCards from 'assets/sounds/table_flip_cards.wav';
// Waitlist "Table Ready" alert cues (warm rising-chime direction). Placeholder
// synths — swap the files in-place to rebrand the audio.
import waitlistReping from 'assets/sounds/waitlist_reping.wav';
import waitlistSeatReady from 'assets/sounds/waitlist_seat_ready.wav';

/**
 * Card-reveal cues, trimmed past their leading silence.
 *
 * Both card assets ship with dead air before any audible content — measured off
 * the waveforms at a 1%-of-full-scale threshold:
 *
 *   table_flip_cards.wav   421ms long,  89ms silent
 *   player_flip_cards.wav  597ms long, 138ms silent
 *
 * For comparison the rest of the set is 0-60ms (player_wins is 0). That silence
 * is why the card-reveal cues sounded late no matter how the TRIGGER was timed —
 * the delay was in the file, not the code. Retiming the trigger was attempted
 * three times and could never have worked.
 *
 * Playing these as sprites starts playback at the onset instead. The start is
 * backed off by SFX_ONSET_SAFETY_MS so a soft attack ahead of the detection
 * threshold is not clipped.
 */
const SFX_ONSET_SAFETY_MS = 15;

const trimmedSprite = (leadMs: number, totalMs: number) => {
  const start = Math.max(0, leadMs - SFX_ONSET_SAFETY_MS);
  return { start: [start, totalMs - start] as [number, number] };
};

/** `table_flip_cards.wav` — community-card flip. */
export const tableFlipCardsSprite = trimmedSprite(89, 421);
/** `player_flip_cards.wav` — hole-card showdown flip. */
export const playerFlipCardsSprite = trimmedSprite(138, 597);
/** Sprite id both of the above use. */
export const SFX_SPRITE_ID = 'start';

/**
 * THE LOUDNESS LADDER — measured, not aspirational (re-levelled 2026-08-28).
 *
 * Loudness is the RMS of a cue's loudest 200ms window. The TIER NAMES say what
 * a cue is for — who it addresses, and whether it may cross tables. The NUMBERS
 * are what each file measures today, after the owner's pass:
 *
 *   SUMMONS — reaches you on a table you are NOT looking at
 *     player_time_warning  −20.4   the 5-4-3-2-1 countdown
 *     waitlist pair        −26.0   your seat is ready (audible in the lobby too)
 *     players_turn         −26.9   your turn starts
 *
 *   OUTCOMES — your money moment
 *     collect_chips        −20.9
 *     player_wins          −22.5   (previously peaked at 0 dBFS — was clipping)
 *
 *   TABLE TALK — actions on the table you are watching
 *     chips_to_pot         −25.0
 *     small_bet            −25.0
 *     bet_chips            −26.7
 *     placing_bet          −30.5
 *
 *   WHISPER — card motion and the fold
 *     fold                 −31.0
 *     both card flips      −31.8
 *     dealing_cards        −35.2
 *     check                −35.3
 *
 *   Not implemented: table_card −31.0, shuffle −29.4.
 *
 * Two things the numbers say that the tier names do not. The countdown is now
 * the loudest cue in the app, which is deliberate — it is the only cue that is
 * time-critical. And the tiers no longer sort cleanly by volume: players_turn
 * (−26.9) sits just under chips_to_pot (−25.0), because the owner cut the
 * turn-start ping hardest. That is fine in play, because an OPPONENT's action
 * cue also carries OPPONENT_ACTION_VOLUME (0.3, i.e. −10.5 dB) on top of the
 * figures above — opponents' chips land near −37, well under every summons.
 *
 * Re-levelling a cue = a plain gain (ffmpeg `volume=XdB`), never a re-encode,
 * so durations and the flip-card sprite offsets stay valid.
 */
export const audioPath = {
  smallBet,
  betChips,
  collectChips,
  placingBet,
  check,
  chipsToPot,
  dealingCards,
  fold,
  playerFlipCards,
  playerTimeWarning,
  playerWins,
  playersTurn,
  shuffle,
  tableCard,
  tableFlipCards,
  waitlistSeatReady,
  waitlistReping
};

/**
 * How far an OPPONENT's action cue sits under the player's own: same sample,
 * so the action→sound pairing stays consistent and learnable, but unmistakably
 * in the background. Roughly 70% down (≈ −10.5 dB on top of the ladder figures
 * above — see THE LOUDNESS LADDER, which quotes this constant), the range the
 * owner asked for (8/26).
 *
 * Pass as `volume` to the opponent-side useSound handle wherever an action cue
 * has an own/opponent split (pages/Game.tsx, Table/Bet.tsx, WinnerMsg.tsx).
 */
export const OPPONENT_ACTION_VOLUME = 0.3;
